import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { iconCheckmark, iconChevron } from '@/components/icons';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, buttonStyles, typographyStyles } from '@/styles';

import '@/components/accordion/fg-accordion-divider';
import '@/components/configurator/step-collapsible/fg-collapsible-icon-button';
import '@/components/messages/fg-message';

import { ActionButton } from '@/types';

@customElement('fg-accordion-title')
@localized()
export class FgAccordionTitle extends LitElement {
  @property({ type: Number })
  index = 0;

  @property({ type: String })
  validationMessage?: string | null;

  @property({ type: Boolean })
  showValidationMessage = false;

  @property({ type: String })
  title!: string;

  @property({ type: String })
  a11yTitle!: string;

  @property({ type: Object })
  description?: {
    selectedOption?: string | boolean;
    additionalSelectedOption?: string | boolean;
  };

  @property({ type: String })
  subtitle?: string;

  @property({ type: String })
  editQueryString?: string | null;

  @property({ type: String })
  removeQueryString?: string | null;

  @property({ type: String })
  cancelQueryString?: string | null;

  @property({ type: String })
  collapseQueryString?: string | null;

  @property({ type: Boolean })
  open = false;

  @property({ type: Boolean })
  valid = false;

  updateComponent(queryString: string | null | undefined) {
    if (queryString === null || queryString === undefined) {
      console.error('Missing query string');
      return;
    }
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: { option: { queryString } },
      }),
    );
  }

  render() {
    const actionButtons: ActionButton[] = [];
    if (this.editQueryString) {
      actionButtons.push(ActionButton.EDIT);
    }
    if (this.removeQueryString) {
      actionButtons.push(ActionButton.DELETE);
    }

    return html`
      ${this.index > 0
        ? html`
            <fg-accordion-divider></fg-accordion-divider>
          `
        : nothing}
      <div class="title-wrapper">
        <h2>
          <div
            class="icon success ${classMap({ valid: this.valid })}"
            aria-hidden="true"
          >
            ${iconCheckmark}
          </div>
          <button
            type="button"
            aria-expanded="${this.open}"
            aria-controls="sect_${this.index}"
            id="accordion_${this.index}"
            @click=${() => this.updateComponent(this.collapseQueryString)}
          >
            <div>
              <span class="title">
                <span>${this.title}</span>
                <span class="description" ?hidden=${this.open}>
                  ${this.description !== undefined &&
                  this.description.selectedOption
                    ? html`
                        <span>
                          -
                          <span>${this.description.selectedOption}</span>
                          ${this.description.additionalSelectedOption
                            ? html`
                                <span class="additional-description">
                                  (${this.description.additionalSelectedOption})
                                </span>
                              `
                            : ''}
                        </span>
                      `
                    : nothing}
                </span>
              </span>
            </div>
          </button>
          ${actionButtons.map(actionButton => {
            return html`
              <fg-collapsible-icon-button
                actionButton=${actionButton}
                buttonTitle=${this.a11yTitle}
              ></fg-collapsible-icon-button>
            `;
          })}
          <span class="title-wrapper-end">
            <slot name="title-button"></slot>
            ${this.cancelQueryString !== null &&
            this.cancelQueryString !== undefined
              ? html`
                  <button
                    type="button"
                    class="cancel"
                    @click=${() => this.updateComponent(this.cancelQueryString)}
                  >
                    ${t('actions.cancel')}
                  </button>
                `
              : html`
                  <button
                    class="icon chevron ${classMap({ open: this.open })}"
                    @click=${() =>
                      this.updateComponent(this.collapseQueryString)}
                    tabindex="-1"
                  >
                    <span aria-hidden="true">${iconChevron}</span>
                    <span class="sr-only">
                      ${this.open ? t('actions.collapse') : t('actions.expand')}
                    </span>
                  </button>
                `}
          </span>
        </h2>
        <span class="subtitle">
          ${this.subtitle
            ? html`
                <span>${this.subtitle}</span>
              `
            : nothing}
        </span>
        ${this.validationMessage && this.showValidationMessage
          ? html`
              <fg-message .message=${this.validationMessage}></fg-message>
            `
          : nothing}
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    typographyStyles,
    css`
      h2 {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        min-height: 32px;
        padding-right: 24px;
        position: relative;
      }
      .description {
        font-family: var(
          --fg-font-family--serif,
          var(--fg-fallback-font-family--serif)
        );
        font-weight: 400;
        font-size: 1.125rem;
      }

      .description > span {
        margin-right: 20px;
      }

      .description span {
        display: inline-block;
      }

      .description span::first-letter {
        text-transform: uppercase;
      }

      .description span.additional-description {
        font-size: var(--fg-font-size--sm, var(--fg-fallback-font-size--sm));
        margin-right: 0;
      }

      .subtitle {
        display: flex;
        font-size: 1rem;
        width: 100%;
      }

      .subtitle:has(span) {
        margin-top: 8px;
      }

      .subtitle.valid {
        margin-left: 32px;
      }

      .cancel {
        color: var(--fg-primary, var(--fg-fallback-primary));
        font-family: var(
          --fg-font-family--serif,
          var(--fg-fallback-font-family--serif)
        );
        font-size: 1rem;
        font-weight: 400;
        margin-right: -24px;
        text-decoration: underline;
      }

      .cancel:hover {
        color: var(--fg-primary--dark, var(--fg-fallback-primary--dark));
      }

      .icon {
        width: 24px;
        height: 24px;
        display: inline-grid;
        place-items: center;
        flex-shrink: 0;
      }

      .icon.success {
        display: none;
      }

      .icon.success.valid {
        display: inline;
      }

      .icon.success svg {
        color: var(--fg-message-success, var(--fg-fallback-message-success));
        max-height: 14px;
        max-width: 14px;
        height: 100%;
        width: 100%;
      }

      .icon.delete {
        color: var(--fg-error, var(--fg-fallback-error));
      }

      .title-wrapper-end {
        display: flex;
        margin: 0 0 0 auto;
        gap: 4px;
      }

      .icon.chevron {
        position: absolute;
        right: 0;
        top: 4px;
        transform: rotate(90deg);
      }

      .icon.chevron.open {
        top: 4px;
        transform: rotate(180deg);
      }

      @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX - 1}px) {
        .title {
          padding-right: 24px;
        }
        .title-wrapper-end {
          margin: 0;
        }
      }
    `,
  ];
}
